import { doc, getFirestore, serverTimestamp, updateDoc } from 'firebase/firestore'
import type { Member, SubKreweTermsAcceptance } from '~/shared/types/member.js'
import type { SubKrewe } from '~/shared/types/subkrewe.js'
import { subkreweHasTerms } from '~/shared/utils/subkreweTerms.js'

/**
 * Record that a member has accepted the T&Cs of a SubKrewe.
 *
 * Writes `subkrewe_terms_acceptances.{subkreweId}` on the member document.
 * The `terms_updated_at` snapshot mirrors the SubKrewe's current value so
 * future edits to the terms invalidate this acceptance.
 *
 * Uses a dotted-path update so the rest of the acceptances map is
 * preserved (members may have acceptances for SubKrewes they previously
 * belonged to).
 */
export function useSubkreweTerms() {
  const membersStore = useMembersStore()

  async function recordAcceptance(member: Member, subkrewe: SubKrewe): Promise<boolean> {
    if (!member?.id || !subkrewe?.id) return false
    if (!subkreweHasTerms(subkrewe)) return true

    const db = getFirestore()
    const memberRef = doc(db, 'members', member.id)

    const acceptance: SubKreweTermsAcceptance = {
      accepted_at: serverTimestamp(),
    }
    if (subkrewe.terms_updated_at) {
      // Prefer the SubKrewe's persisted timestamp; fall back to now if the
      // terms were just stamped client-side and still hold a sentinel.
      acceptance.terms_updated_at = subkrewe.terms_updated_at instanceof Date
        ? subkrewe.terms_updated_at
        : serverTimestamp()
    }

    try {
      await updateDoc(memberRef, {
        [`subkrewe_terms_acceptances.${subkrewe.id}`]: acceptance,
        updated_at: serverTimestamp(),
      })

      // Patch the store so UI reacts without waiting for a round-trip.
      const path = `members/${member.id}`
      membersStore.$patch((state) => {
        const existing = state.members[path]
        if (!existing) return
        const next: Record<string, SubKreweTermsAcceptance> = {
          ...(existing.subkrewe_terms_acceptances || {}),
          [subkrewe.id]: {
            accepted_at: new Date(),
            terms_updated_at: subkrewe.terms_updated_at instanceof Date
              ? subkrewe.terms_updated_at
              : new Date(),
          },
        }
        existing.subkrewe_terms_acceptances = next
      })

      return true
    }
    catch (error) {
      console.error('[useSubkreweTerms] Failed to record acceptance:', error)
      return false
    }
  }

  return {
    recordAcceptance,
  }
}
